import * as api from "./client.js";

export type PackageRef = {
  id: string;
  productCode: string;
  name: string;
  version: number;
  isActive: boolean;
};

export type CrmPackageMapping = {
  id: string;
  crmPackageId: string;
  action: "map" | "skip";
  packageId: string | null;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  package: PackageRef | null;
};

export type CrmPackageMappingsListResponse = {
  mappings: CrmPackageMapping[];
  total: number;
};

export type CrmPackageMappingSingleResponse = {
  mapping: CrmPackageMapping;
};

export type CreateCrmPackageMappingRequest = {
  crmPackageId: string;
  action?: "map" | "skip";
  packageId?: string;
  description?: string | null;
};

export type UpdateCrmPackageMappingRequest = {
  crmPackageId?: string;
  action?: "map" | "skip";
  packageId?: string | null;
  description?: string | null;
};

export type BulkSkipRequest = {
  entries: Array<{
    crmPackageId: string;
    description?: string | null;
  }>;
};

export type BulkSkipResponse = {
  created: number;
  skipped: number;
};

export const crmPackageMappings = {
  getAll: async (filters?: {
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<CrmPackageMappingsListResponse> => {
    const params = new URLSearchParams();
    if (filters?.search) params.append("search", filters.search);
    if (filters?.limit !== undefined) params.append("limit", filters.limit.toString());
    if (filters?.offset !== undefined) params.append("offset", filters.offset.toString());

    const query = params.toString() ? `?${params.toString()}` : "";
    return api.get<CrmPackageMappingsListResponse>(`/v1/fulfilment/crm-package-mappings${query}`);
  },

  getById: async (id: string): Promise<CrmPackageMapping> => {
    const response = await api.get<CrmPackageMappingSingleResponse>(
      `/v1/fulfilment/crm-package-mappings/${id}`,
    );
    return response.mapping;
  },

  create: async (data: CreateCrmPackageMappingRequest): Promise<CrmPackageMapping> => {
    const response = await api.post<CrmPackageMappingSingleResponse>(
      "/v1/fulfilment/crm-package-mappings",
      data,
    );
    return response.mapping;
  },

  createSkip: async (
    crmPackageId: string,
    description?: string | null,
  ): Promise<CrmPackageMapping> => {
    const response = await api.post<CrmPackageMappingSingleResponse>(
      "/v1/fulfilment/crm-package-mappings",
      { crmPackageId, action: "skip", description: description ?? null },
    );
    return response.mapping;
  },

  bulkSkip: async (
    entries: Array<{ crmPackageId: string; description?: string | null }>,
  ): Promise<BulkSkipResponse> => {
    return api.post<BulkSkipResponse>("/v1/fulfilment/crm-package-mappings/bulk-skip", {
      entries,
    });
  },

  update: async (id: string, data: UpdateCrmPackageMappingRequest): Promise<CrmPackageMapping> => {
    const response = await api.put<CrmPackageMappingSingleResponse>(
      `/v1/fulfilment/crm-package-mappings/${id}`,
      data,
    );
    return response.mapping;
  },

  delete: async (id: string): Promise<void> => {
    await api.del(`/v1/fulfilment/crm-package-mappings/${id}`);
  },
};
